import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Tenant } from '@app/Models/tenant';
import { TenantEnvironmentDetails } from '@app/Models/tenant-environment-details.model';
import { TenantGroups } from '../../../Enums';
import { Permissions } from '../../../Models/Permissions';
import { TenantGroup } from '../../../Models/tenant-group.model';
import { TenantProperty } from '../../../Models/tenantProperty';
import { TenantService } from '../../../Services';
import { TenantEnvironmentService } from '@app/Services/tenant-environment.service';
import { PermissionService } from '../../../Services/permission.service';
import { Router } from '@angular/router';
import { Subject, forkJoin } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { CopyConfigService } from '../Airsweb/Services';
import { MatCard, MatCardTitle, MatCardSubtitle, MatCardContent } from '@angular/material/card';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/divider';
import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { MatCheckbox } from '@angular/material/checkbox';
import { TenantCommentsComponent } from '../tenant-comments/tenant-comments.component';

@Component({
    selector: 'my-package',
    templateUrl: './tenantoverview.component.html',
    styleUrls: ['./tenantoverview.component.less'],
    imports: [MatCard, MatCardTitle, MatCardSubtitle, MatCardContent, MatButton, MatIcon, MatDivider, MatFormField, MatLabel, MatSelect, FormsModule, MatOption, MatInput, MatCheckbox, TenantCommentsComponent]
})
export class TenantOverviewComponent implements OnInit, OnDestroy {
    errorMessage: string;
    tenantStatuses: TenantProperty[];
    selectedTenantStatus: TenantProperty;
    tenantTypes: TenantProperty[];
    selectedTenantType: TenantProperty;
    tenantGroups: TenantGroup[];
    selectedTenantPermissionGroup: TenantGroup;
    allowEcoTranslateSynchronisation: boolean;
    readonly permissions = Permissions;
    showTenantPermissionGroup = true;
    environmentDetails: TenantEnvironmentDetails;
    sanitizedGraphEndpoint: SafeUrl;
    provisionJobId;
    organisationId: string | null = null;
    displayOrganisationId: boolean;
    tenantIndustries: TenantProperty[];
    tenantSizes: TenantProperty[];
    selectedTenantIndustry: TenantProperty;
    selectedTenantSize: TenantProperty;
    bestPractice: boolean;
    hasCopyConfigLogs: boolean = false;
    warehouseEnabled: boolean;
    private isDestroyed$: Subject<void> = new Subject();

    constructor(
        private route: ActivatedRoute,
        private tenantService: TenantService,
        private tenantEnvironmentService: TenantEnvironmentService,
        private permissionService: PermissionService,
        private router: Router,
        private sanitizer: DomSanitizer,
        private copyConfigService: CopyConfigService,
    ) { }

    get selectedTenant(): Tenant {
        return this.tenantService.selectedTenant;
    }

    get selectedTenantHasAirswebApplication(): boolean {
        return this.tenantService.selectedTenantHasAirswebApplication;
    }


    ngOnInit(): void {
        this.sanitizedGraphEndpoint = this.sanitizer.bypassSecurityTrustUrl(this.tenantService.selectedTenant.GraphEndpoint);
        this.getEnvironmentDetails();
        this.getTenantStatuses();
        this.getTenantTypes();
        this.getTenantPermissionGroups();
        this.getAllowEcoTranslateSynchronisation();
        this.getProvisionJobForTenant();
        this.getOrganisationId();
        this.getCopyConfigLogs();

        if (this.route.parent) {
            this.route.parent.params.subscribe(() => {
                this.getTenantAttributes();
            });
        }
    }

    ngOnDestroy(): void {
        this.isDestroyed$.next();
    }

    hasPermission(permission: string): boolean {
        return this.permissionService.hasPermission(permission);
    }

    accessAva(): void {
        this.tenantService.access(this.tenantService.selectedTenant)
            .subscribe(
                avaUrl => window.open(avaUrl.toString()),
                err => { console.log(err); this.errorMessage = err }
            );
    }

    goToProvisionLogs(): void {
        this.router.navigate(['jobs', this.provisionJobId]);
    }

    goToCopyConfigLogs(): void {
        this.router.navigate([`tenant/${this.selectedTenant.TenantId}/airsweb/copyconfig/logs`]);
    }

    onTenantStatusSelect(): void {
        this.tenantService.updateTenantStatus(
            this.tenantService.selectedTenant.Value,
            this.selectedTenantStatus.Value);
    }

    onTenantTypeSelect(): void {
        this.tenantService.updateTenantType(
            this.tenantService.selectedTenant.Value,
            this.selectedTenantType.Value);
    }

    onTenantPermissionGroupSelect(): void {
        this.tenantService.updateTenantPermissionGroup(
            this.tenantService.selectedTenant.Value,
            this.selectedTenantPermissionGroup.name);
    }

    onAllowEcoTranslateSynchronisationChange(): void {
        this.tenantService.updateEcoTranslateSynchronisation(
            this.tenantService.selectedTenant.Value,
            this.allowEcoTranslateSynchronisation);
    }

    onBestPracticeChange(): void {
        this.tenantService.updateTenant(this.selectedTenant);
    }

    getTenantStatuses(): void {
        this.tenantService.getTenantStatuses()
            .subscribe(
                statuses => {
                    this.tenantStatuses = statuses;
                    this.getCurrentTenantStatus();
                },
                err => this.errorMessage = err
            );
    }

    getTenantAttributes(): void {
        forkJoin([
            this.tenantService.getTenantIndustries(),
            this.tenantService.getTenantSizes(),
            this.tenantService.getTenantAttributes(this.selectedTenant.Value)])
            .pipe(take(1))
            .subscribe(([industries, sizes, attributes]) => {
                this.tenantIndustries = industries.map((industry) => ({ Text: industry, Value: industry }));
                this.tenantSizes = sizes.map((size) => ({ Text: size, Value: size }));
                this.selectedTenantIndustry = this.tenantIndustries.find((industry) => industry.Value === this.selectedTenant.Industry);
                this.selectedTenantSize = this.tenantSizes.find((size) => size.Value === this.selectedTenant.Size);
                this.warehouseEnabled = attributes.WarehouseEnabled;
            });
    }

    displayOrganisationInformation(): boolean {
        return this.displayOrganisationId;
    }

    async updateTenantAttributes(): Promise<any> {
        const tenant = this.selectedTenant;
        await this.tenantService.updateTenantAttributes(tenant, {
            crmId: tenant.CrmId,
            crmName: tenant.CrmName,
            industry: this.selectedTenantIndustry?.Value,
            companySize: this.selectedTenantSize?.Value,
            WarehouseEnabled: this.warehouseEnabled
        })
    }

    private getTenantTypes(): void {
        this.tenantService.getTenantTypes()
            .subscribe(
                types => {
                    this.tenantTypes = types;
                    this.getCurrentTenantType();
                },
                err => this.errorMessage = err
            );
    }

    private getTenantPermissionGroups(): void {
        this.tenantGroups = this.permissionService.allowedTenantGroups
            .filter(group => group !== TenantGroups.Master)
            .map((group) => ({
                description: group,
                grantedAutomaticallyWithAdmin: false,
                name: group,
                displayName: TenantGroups[group],
                permissions: 1
            }));
        this.getCurrentTenantPermissionGroup();
    }

    private getProvisionJobForTenant(): void {
        this.tenantService.getCurrentTenantProvisionJob().subscribe((provisionJob) =>
            this.provisionJobId = provisionJob
        );
    }

    private getCurrentTenantStatus(): void {
        this.tenantService.currentTenantStatus()
            .subscribe(
                result => this.selectedTenantStatus = this.tenantStatuses.find(x => x.Value === result),
                err => this.errorMessage = err
            );
    }

    private getCurrentTenantType(): void {
        this.tenantService.currentTenantType()
            .subscribe(
                result => this.selectedTenantType = this.tenantTypes.find(x => x.Value === result),
                err => this.errorMessage = err
            );
    }

    private getCurrentTenantPermissionGroup(): void {
        this.tenantService.currentTenantPermissionGroup()
            .subscribe(
                result => this.selectedTenantPermissionGroup = this.tenantGroups.find(x => x.name === result),
                err => this.errorMessage = err
            );
    }

    private getAllowEcoTranslateSynchronisation(): void {
        this.tenantService.currentEcoTranslateSynchronisation()
            .subscribe(
                result => this.allowEcoTranslateSynchronisation = result,
                err => this.errorMessage = err
            );
    }

    private getEnvironmentDetails(): void {
        this.tenantEnvironmentService.currentEnvironmentDetails()
            .subscribe((result: TenantEnvironmentDetails) => {
                this.environmentDetails = result;
            });
    }

    private getOrganisationId(): void {
        this.tenantService.currentTenantOrganisationId().subscribe((organisationId) => {
            const emptyGuid = "00000000-0000-0000-0000-000000000000";
            this.organisationId = organisationId;
            this.displayOrganisationId = (organisationId && organisationId !== emptyGuid);
        }
        );
    }

    private getCopyConfigLogs(): void {
        this.copyConfigService
            .getCopyConfigLogs(this.selectedTenant.TenantId)
            .pipe(takeUntil(this.isDestroyed$))
            .subscribe(result => {
                if (result && result.length > 0) {
                    this.hasCopyConfigLogs = true;
                }
            });
    }
}
